import React, { useEffect, useState, Suspense } from 'react';
import PropTypes from 'prop-types';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import { Helmet } from 'react-helmet-async';
import { useTenant } from 'src/auth/context/firebase/TenantContext';
import { useSettingsContext } from 'src/components/settings';
import PowerBIReport from 'src/components/powerbi-embed/PowerbiEmbed';
import { LoadingScreen } from 'src/components/loading-screen';
import axios from 'axios';
import axiosInstance from 'src/utils/axios';

const DynamicPage = ({ title, Component, reportIds, reportMonth }) => {
  const settings = useSettingsContext();
  const [loadedReportIds, setLoadedReportIds] = useState(reportIds || []);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const { selectedTenant } = useTenant();

  useEffect(() => {
    if (!selectedTenant) {
      return;
    }

    if (!reportIds || reportIds.length === 0) {
      setLoading(true);

      const apiEndpoint = `${process.env.REACT_APP_API_BASE_URL}/api/Page/${selectedTenant}_${title}`;

      axiosInstance
        .get(apiEndpoint)
        .then((response) => {
          setLoadedReportIds(response.data.reportIds);
        })
        .catch((err) => {
          setError(err);
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [selectedTenant, title, reportIds]);

  if (!selectedTenant) {
    return (
      <Container
        maxWidth={settings.themeStretch ? false : 'xl'}
        sx={{ marginTop: 0, paddingTop: 2 }}
      >
        <Typography variant="h6">Please select a tenant to view the reports.</Typography>
      </Container>
    );
  }

  if (loading) {
    return (
      <Container
        maxWidth={settings.themeStretch ? false : 'xl'}
        sx={{ marginTop: 0, paddingTop: 2 }}
      >
        <CircularProgress />
      </Container>
    );
  }

  if (error) {
    return (
      <Container
        maxWidth={settings.themeStretch ? false : 'xl'}
        sx={{ marginTop: 0, paddingTop: 2 }}
      >
        <div>Error loading reports: {error.message}</div>
      </Container>
    );
  }

  return (
    <>
      <Helmet>
        <title>{title}</title>
      </Helmet>
      <Container maxWidth={settings.themeStretch ? false : 'xl'} sx={{ marginTop: 2, padding: 2 }}>
        <Suspense fallback={<LoadingScreen />}>
          <Component />
        </Suspense>
        {loadedReportIds.map((reportId, index) => (
          <PowerBIReport key={index} reportId={reportId} />
        ))}
        {reportMonth && (
          <Typography variant="body1" sx={{ marginTop: 2 }}>
            Report Month: {reportMonth}
          </Typography>
        )}
      </Container>
    </>
  );
};

DynamicPage.propTypes = {
  title: PropTypes.string.isRequired,
  Component: PropTypes.elementType.isRequired,
  reportIds: PropTypes.arrayOf(PropTypes.string),
  reportMonth: PropTypes.string,
};

export default DynamicPage;
